import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { z } from 'zod';
import { EventDetails } from '../../pages/EventDetails';
import { Header } from '../../components/Header';
import { useEvent } from '@/lib/hooks/useEvents';
import { useUserRsvp } from '@/lib/hooks/useRsvps';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { useImagePreload } from '@/lib/hooks/useImagePreload';
import { useNotificationOpenTracking } from '@/lib/hooks/useUserNotifications';

const eventDetailsSearchSchema = z.object({
  notificationId: z.string().optional(),
  from: z.enum(['notifications']).optional(),
});

export const Route = createFileRoute('/_authenticated/events/$eventId')({
  validateSearch: eventDetailsSearchSchema,
  component: EventDetailsPage,
});

function EventDetailsPage() {
  const { eventId } = Route.useParams();
  const { notificationId, from } = Route.useSearch();
  const { user } = useAuth();
  const { isAdmin, isLoading: isLoadingProfile } = useUserProfile();
  const navigate = useNavigate();
  const eventIdNum = parseInt(eventId, 10);
  const router = useRouterState();
  const backTo = from === 'notifications' ? '/notifications' : undefined;
  const childRouteIds = new Set([
    '/_authenticated/events/$eventId/edit',
    '/_authenticated/events/$eventId/attendance',
  ]);
  const isChildRoute = router.matches.some((match) =>
    childRouteIds.has(match.routeId)
  );

  const {
    data: event,
    isLoading: isLoadingEvent,
    error: eventError,
  } = useEvent(eventIdNum);
  const { data: rsvp, isLoading: isLoadingRsvp } = useUserRsvp(
    eventIdNum,
    user?.id || ''
  );

  // Preload event header background image while data loads
  const headerImageLoaded = useImagePreload(
    '/assets/images/event-header-bg.png'
  );

  useNotificationOpenTracking(notificationId, () => {
    navigate({
      to: '/events/$eventId',
      params: { eventId },
      replace: true,
    });
  });

  // Render child routes (edit, attendance, etc.)
  if (isChildRoute) {
    return <Outlet />;
  }

  if (
    isLoadingEvent ||
    isLoadingRsvp ||
    isLoadingProfile ||
    !headerImageLoaded
  ) {
    return (
      <>
        <Header enableBack backTo={backTo} fallbackTo="/events" />
        <div className="flex justify-center items-center py-12">
          <p className="text-body text-foreground/60">Loading event...</p>
        </div>
      </>
    );
  }

  if (eventError || !event) {
    return (
      <>
        <Header enableBack backTo={backTo} fallbackTo="/events" />
        <div className="flex justify-center items-center py-12">
          <p className="text-body text-foreground/60">
            {eventError ? 'Failed to load event' : 'Event not found'}
          </p>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <Header
        enableBack
        backTo={backTo}
        fallbackTo="/events"
        additionalClasses="bg-slate-600"
      />
      <EventDetails
        event={event}
        rsvp={rsvp || null}
        userId={user?.id || null}
        isAdmin={isAdmin()}
      />
    </div>
  );
}
